import React, { useState } from 'react';
import Button from './ui/Button';
import Icon from './AppIcon';
import { getAuthHeaders } from '../lib/api';

/**
 * Final intentional switch: turn messaging on (even if A2P / "the legal stuff" is pending).
 */
export default function GoLiveOnboarding({
  onComplete,
  a2pApproved = false,
  touchpointsPaused = true,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/touchpoints-pause', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ paused: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not turn on messaging');
      }
      onComplete?.({ wentLive: true, a2pApproved });
    } catch (err) {
      setError(err.message || 'Could not turn on messaging');
    } finally {
      setBusy(false);
    }
  };

  const keepPaused = () => {
    onComplete?.({ wentLive: false, keptPaused: true });
  };

  return (
    <div className="max-w-md mx-auto text-center p-6">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="Rocket" size={32} className="text-primary" />
      </div>

      <h2 className="text-2xl font-bold text-foreground mb-2">Turn on messaging</h2>

      {a2pApproved ? (
        <p className="text-muted-foreground mb-6 text-sm">
          You&apos;re cleared to send. Turn messaging on to start your monthly cadence. Past clients
          who haven&apos;t gotten a text yet get your intro instead of the first month&apos;s tip.
          We&apos;ll text you when clients reply.
        </p>
      ) : (
        <p className="text-muted-foreground mb-6 text-sm">
          You can turn messaging on now. Texts won&apos;t go out until{' '}
          <span className="text-foreground font-medium">the legal stuff</span> finishes
          (carrier approval — often about 1–2 business days, sometimes up to ~10–15). Once
          that&apos;s done, sending starts automatically and we&apos;ll text you when clients reply.
        </p>
      )}

      {!touchpointsPaused && (
        <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
          Messaging is already on.
        </p>
      )}

      {error && (
        <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <Button onClick={turnOn} loading={busy} disabled={busy} className="w-full">
          {touchpointsPaused ? 'Turn on messaging' : 'Continue'}
        </Button>
        {touchpointsPaused && (
          <button
            type="button"
            onClick={keepPaused}
            disabled={busy}
            className="w-full text-sm text-muted-foreground hover:text-foreground"
          >
            Keep paused
          </button>
        )}
        <p className="text-xs text-muted-foreground">
          You can pause or resume anytime on Touchpoints.
        </p>
      </div>
    </div>
  );
}
